import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import FigureResults, { figureApprovalError } from './FigureResults';
/**
 * VariationReviewCard — AI 변형(⑤)·생성(⑥) 검수 카드.
 *
 * 계약: BE-20260825-ai-variation-screens.md §5 · problem-hub-integrated.md §7-2.
 * 분석 검수(슬롯 4개 채우기)와 정보 구조가 다르다 — 여기는 "새 문제 전체 검토 + 원본 대조".
 * 생성은 원본이 없다는 것만 다르므로(무에서 출제) 같은 카드를 kind 로 갈라 쓴다.
 *
 * 정본 = result(원본 산출·불변) 위에 result_edited(검수 수정본)를 덮은 병합본. 승인도 이 병합본을 저장한다.
 * 저장은 보낸 키만 병합, null 이면 그 수정 철회(원본 복귀) — 분석 v2와 같은 규약.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Check, Trash2, RotateCw, AlertTriangle, Eye, ArrowUpRight } from 'lucide-react';
import { Button, MathceptDetailModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { BodyPayloadView, SolutionPayloadView, CIRCLED } from '../OcrHub/BlockRenderer';
import parseAnswerForDisplay from '../OcrHub/answerLatex';
import { InlineMath } from 'react-katex';
// 본문·풀이·개념을 **문제은행과 같은 부품**으로 그린다(오너 지시 2026-08-30 「완전히 디자인이
// 동일하게」). 검수 산출은 Block/Run 이고 은행은 Lexical 이라, 종전에는 같은 문제가 승인 전후로
// 다른 렌더러에 걸려 글자 크기·선지 배치·수식 배율이 전부 갈렸다. 산출을 은행의 모양으로
// 바꿔(payloadToLexical) [문제 보기] 탭이 쓰는 편집기·배지를 그대로 쓴다.
import OcrLexicalEditor from '../OcrHub/OcrLexicalEditor';
import { ConceptBadge, matchConcept, fetchConceptDictV2, fetchConceptDetailV2 } from '../ExamPapersV2/conceptBadge';
// 칩 안의 번호도 표와 같은 얼굴(고정폭·굵기) — 색은 칩이 갖는다(오너 결정 2026-09-01)
import ProblemIdLink from './ProblemIdLink';
import { bodyPayloadToLexical, solutionPayloadToLexical, collectLexicalImageSrcs, collectImageIssues } from './payloadToLexical';
import VariationPreviewModal from './VariationPreviewModal';
import {
  fetchVariation, patchVariation, approveVariation, rejectVariation, retryVariation,
  fetchGeneration, patchGeneration, approveGeneration, rejectGeneration, retryGeneration,
  getJobStatusDisplay, getReviewStatusDisplay, getTypeLabel, IMAGE_MODE_LABEL,
  ANSWER_TYPE_LABEL, CHOICE_SUBTYPE_LABEL,
  POLLING_STATUSES, JOB_POLL_MS, AUTH_REDIRECT } from './variationApi';
import ConceptPicker from './ConceptPicker';
import { splitSourceLatex } from './sourceLatex';
import { difficultyOptionLabel, difficultyBadgeClass, fetchProblem } from './problemApi';

// kind 별 API 묶음 — 두 기능이 같은 규약이라 호출부만 갈아 끼운다
const API = {
  variation: { fetch: fetchVariation, patch: patchVariation, approve: approveVariation, reject: rejectVariation, retry: retryVariation },
  generation: { fetch: fetchGeneration, patch: patchGeneration, approve: approveGeneration, reject: rejectGeneration, retry: retryGeneration },
};

/**
 * 개념 이름 찾기 — **배열이 정본이다.**
 *
 * 계약 §4(D8)로 변형·생성 상세와 PATCH 응답 최상위에 `concept_names:[{concept_id,name}]` 가
 * **항상** 실린다(빈 배열 포함, `{...result, ...result_edited}` 병합본의 concept_ids 기준).
 * 그래서 예전에 함께 흡수하던 맵 형태({"425":"마름모"})는 뺐다 — 맵을 주는 자리는 분석 검수(④)의
 * `current` 뿐이고(계약 §2-2 가 레거시 맵을 그대로 둔다), 그 화면은 AnalysisReviewCard.jsx:71 이
 * 따로 그린다. 이 카드는 변형·생성 상세만 읽으므로 맵이 올 길이 없다.
 *
 * 사전에 없는 id 는 `name:null` 로 오므로, 그때는 호출부가 "#425" 로 떨어뜨린다.
 */
function conceptNameOf(detail, cid) {
  const src = detail?.concept_names;
  if (!Array.isArray(src)) return null;
  const hit = src.find((c) => String(c?.concept_id) === String(cid));
  return hit?.name ?? null;
}

/** result 위에 result_edited 를 덮은 정본 */
function mergeValues(detail) {
  const base = detail?.result || {};
  const edit = detail?.result_edited || {};
  return { ...base, ...edit };
}

function AnswerLine({ value }) {
  const shown = parseAnswerForDisplay(value);
  if (shown.mode === 'empty') return <span className="text-gray-400">정답 없음</span>;
  if (shown.mode === 'text') return <span className="font-medium text-gray-900">{shown.value}</span>;
  return (
    <span className="font-medium text-gray-900">
      <InlineMath math={shown.value} renderError={() => <code className="rounded bg-gray-100 px-1 text-[0.9em]">{value}</code>} />
    </span>
  );
}

/**
 * 원본 평문 — 수식 조각만 KaTeX 로 그린다(나누는 규칙은 sourceLatex.js).
 *
 * 바로 아래 「변형 결과」가 KaTeX 로 그려지므로 **여기도 같은 얼굴이어야 대조가 된다.**
 * 날 LaTeX 로 두면 같은 문장이 위아래에서 다르게 생겨, 어디가 바뀐 것인지 글자를 세어 가며
 * 읽어야 했다.
 *
 * 렌더가 실패하면 구분자까지 붙은 원문을 그대로 남긴다 — 대조가 목적이라, 못 그린 자리가
 * 비어 버리면 지금보다 나쁘다. 줄바꿈은 보기 번호 (1)(2)… 를 나누므로 pre-wrap 을 지킨다.
 */
function SourceTextView({ text }) {
  const parts = useMemo(() => splitSourceLatex(text), [text]);
  return (
    <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-gray-600">
      {parts.map((p, i) => (p.math ? (
        <InlineMath
          // eslint-disable-next-line react/no-array-index-key -- 조각은 text 에서만 파생돼 순서가 곧 정체다
          key={i}
          math={p.value}
          renderError={() => <code className="rounded bg-gray-100 px-1 text-[0.9em]">{p.raw}</code>}
        />
      ) : (
        // eslint-disable-next-line react/no-array-index-key -- 위와 같다
        <span key={i}>{p.value}</span>
      )))}
    </p>
  );
}

/**
 * @param canApprove [승인]·[폐기] 를 세울지 — 서버가 교사에게 403 으로 막은 경로들이다
 *   (변형 `approve`·`reject`, 생성 `approve`·`reject` — BE-20260910 회신 §1-2).
 * @param canRunAi 실패한 건의 [다시 생성] 을 세울지 — 서버는 열려 있고 **LLM 비용** 때문에 화면에서만 감춘다.
 *
 * 이 카드는 세 화면이 함께 쓴다(변형 탭 · AI 문제 생성 창 · 시험지 배치 화면). 그래서 판정을
 * 여기서 읽지 않는다 — 담는 쪽이 정해 내려 준다. 기본값이 허용(true)인 이유도 그것이다:
 * prop 을 안 넘기는 마운트 지점(BatchPanel 등)에서 버튼이 **말없이 사라지지 않게** 한다.
 *
 * [저장](PATCH)은 교사에게 열려 있다 — 여기서 가르지 않는다.
 */
function VariationReviewCard({
  kind = 'variation', id, sourceProblemId = null, sourceProblem = null,
  onApproved = null, onChanged = null, onOpenProblem = null, onDirtyChange = null,
  canApprove = true, canRunAi = true,
}) {
  const { getAccessToken } = useAuth();
  const api = API[kind];
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null); // 'save'|'approve'|'reject'|'retry'
  const [approvedId, setApprovedId] = useState(null); // 승인으로 만들어진 새 문제 — 이동은 사용자 선택
  const [stale, setStale] = useState(false); // 409 — 서버 쪽이 내가 읽은 뒤로 바뀌었다
  const idRef = useRef(id);
  useEffect(() => { idRef.current = id; }, [id]);

  // 편집 상태 — 정본으로 seed
  const [answerText, setAnswerText] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [description, setDescription] = useState('');
  // 유형·개념도 검수 대상이다(계약 §4-2 "유형이 틀렸다고 판단되면 PATCH 로 고칠 수 있다").
  // 고칠 수단이 없으면 유형 하나 틀린 문제를 통째로 폐기하는 수밖에 없다.
  const [answerType, setAnswerType] = useState('');
  const [choiceSubtype, setChoiceSubtype] = useState('');
  const [conceptIds, setConceptIds] = useState([]);
  const [conceptEdit, setConceptEdit] = useState(false);
  // 개념 배지의 [순번][유형] 재료 · 눌렀을 때 뜨는 설명 — [문제 보기] 탭과 같은 것을 쓴다.
  // 사전을 못 받으면 null 이고, 그때 배지는 이름만 남는다(ConceptBadge 가 그 칸을 안 그린다).
  const [conceptDict, setConceptDict] = useState(null);
  const [conceptDetail, setConceptDetail] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false); // 지면 모양 미리보기
  const seedRef = useRef({ answer: '', difficulty: '', description: '', answerType: '', choiceSubtype: '', conceptIds: [] });

  const seed = useCallback((d) => {
    const v = mergeValues(d);
    const at = v.problem?.answerType ?? '';
    const cs = v.problem?.choiceSubtype ?? '';
    // 개념은 원본 문제에서 승계된다(계약 §4 D1) — 원본에 개념이 없으면 `null` 이 아니라 빈 배열 `[]` 이
    // 온다(회신 §2 3-1-b, 커밋 5d501ef). **그래도 Array.isArray 흡수를 지우지 않는다** — 그 배포
    // 이전에 만들어진 변형 행에는 아직 `null` 이 들어 있고 소급 백필도 하지 않기로 했다(회신 §0).
    // 지우면 옛 행에서 concept_ids.map 이 그대로 터진다.
    const cids = Array.isArray(v.concept_ids) ? v.concept_ids : [];
    setAnswerText(v.answer ?? '');
    setDifficulty(v.difficulty != null ? String(v.difficulty) : '');
    setDescription(v.description ?? '');
    setAnswerType(at);
    setChoiceSubtype(cs);
    setConceptIds(cids);
    setConceptEdit(false);
    seedRef.current = {
      answer: v.answer ?? '',
      difficulty: v.difficulty != null ? String(v.difficulty) : '',
      description: v.description ?? '',
      answerType: at,
      choiceSubtype: cs,
      conceptIds: cids,
    };
  }, []);

  const load = useCallback(async ({ silent = false, keepToast = false } = {}) => {
    if (id == null) return;
    const target = id;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const d = await api.fetch(target, getAccessToken);
      if (String(target) !== String(idRef.current)) return;
      setDetail(d);
      if (d?.status === 200) seed(d);
    } catch (e) {
      if (String(target) !== String(idRef.current)) return;
      if (silent) {
        // 폴링 중이면 다음 tick 이 재시도한다 — 그때만 '자동 재시도'라고 말한다.
        if (!keepToast) setToast({ type: 'error', text: '최신 상태를 읽지 못했습니다 — 새로고침해 주세요.' });
        return;
      }
      setError(e.message || '불러오지 못했습니다.');
      setDetail(null);
    } finally {
      if (String(target) === String(idRef.current) && !silent) setLoading(false);
    }
  }, [id, api, getAccessToken, seed]);

  useEffect(() => { setDetail(null); setToast(null); setStale(false); load(); }, [load]);

  /**
   * 원본 문제의 **본문 Lexical** — 대조용이자 그림 공급원.
   *
   * 종전에는 `source_text`(평문)만 그렸다. 그러면 같은 카드 안에서 위(원본)는 12px 회색 평문에
   * 선지가 `(1)(2)…`, 아래(결과)는 은행과 같은 편집기에 `①②…` 라 **대조가 되지 않았다** —
   * 원본은 은행에 있는 실제 문제이니 그 본문을 받아 같은 얼굴로 그린다.
   *
   * 덤으로 `image_mode:'reuse'` 변형의 그림을 여기서 얻는다(계약 §4 「원본 그림을 그대로 재부착」).
   * 못 받으면 조용히 접고 종전 평문 대조로 돌아간다.
   */
  const [fetchedSource, setFetchedSource] = useState(null);
  useEffect(() => {
    // 부모가 이미 읽어 넘겨 준 상세가 있으면 왕복하지 않는다([변형] 탭이 계보 때문에 이미 읽는다).
    if (kind !== 'variation' || sourceProblem != null || sourceProblemId == null) { setFetchedSource(null); return undefined; }
    let alive = true;
    setFetchedSource(null);
    Promise.resolve(fetchProblem(sourceProblemId, getAccessToken))
      .then((d) => { if (alive) setFetchedSource(d ?? null); })
      .catch(() => { /* 평문 대조로 남는다 */ });
    return () => { alive = false; };
  }, [kind, sourceProblem, sourceProblemId, getAccessToken]);

  const sourceDoc = useMemo(() => {
    const d = sourceProblem ?? fetchedSource;
    const lex = d?.body_lexical ?? null;
    if (!lex) return null;
    return { lexical: lex, images: collectLexicalImageSrcs(lex) };
  }, [sourceProblem, fetchedSource]);

  // 개념 사전(모듈 캐시 5분) — 카드가 여러 개 열려도 왕복은 한 번이다
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken)).then((d) => { if (alive) setConceptDict(d); });
    return () => { alive = false; };
  }, [getAccessToken]);

  /**
   * 개념 배지를 누르면 그 개념의 설명을 띄운다 — ProblemViewTab.openConcept 와 같은 규약이다.
   * 못 받으면 **빈 창을 띄우지 않는다**: 창만 열리면 「설명이 없는 개념」으로 읽힌다.
   */
  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    if (list == null) {
      setConceptDetail(null);
      setToast({ type: 'error', text: '개념 설명을 불러오지 못했습니다.' });
      return;
    }
    setConceptDetail({ loading: false, data: list });
  }, [getAccessToken]);

  // 생성 중이면 폴링(건당 수십 초) — 완료·실패에서 스스로 멈춘다
  const polling = detail != null && POLLING_STATUSES.includes(detail.status);
  const [waitedLong, setWaitedLong] = useState(false);
  useEffect(() => {
    if (!polling) { setWaitedLong(false); return undefined; }
    const t = setInterval(() => load({ silent: true }), JOB_POLL_MS);
    // 3분이 넘으면 문구를 바꾼다 — 계속 "수십 초"라고 말하면 멈춘 작업을 하염없이 기다린다
    const slow = setTimeout(() => setWaitedLong(true), 3 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(slow); };
  }, [polling, load]);

  const values = useMemo(() => mergeValues(detail), [detail]);
  const problem = values.problem || null;
  const solution = values.solution || null;
  const typeLabel = getTypeLabel(problem);
  /**
   * 은행과 같은 얼굴로 그리기 위한 변환 — **그리기 전용이고 저장에는 쓰이지 않는다**
   * (저장은 아래 buildPatch 가 원본 Block/Run 을 그대로 보낸다).
   * 변환이 못 하는 형태면 null 이고, 그때는 종전 Block/Run 렌더러로 강등한다 —
   * 검수 화면에서 본문이 통째로 사라지는 것이 가장 나쁘다.
   */
  // `reuse` 변형은 원본 그림을 그대로 재부착해 등록된다(계약 §4). 검수 산출에는 그 픽셀이
  // 없으므로 원본에서 같은 순서로 빌려 채운다 — 안 그러면 그림이 있는 문제를 그림 없이 검수한다.
  const reuseImages = !FIGURE_SVG_EDITOR && detail?.image_mode === 'reuse' ? (sourceDoc?.images ?? null) : null;
  const bodyLex = useMemo(() => {
    try { return bodyPayloadToLexical(problem, { fallbackImages: reuseImages }); } catch (e) { console.error('[VariationReviewCard] 본문 변환 실패', e); return null; }
  }, [problem, reuseImages]);
  const solutionLex = useMemo(() => {
    try { return solutionPayloadToLexical(solution); } catch (e) { console.error('[VariationReviewCard] 풀이 변환 실패', e); return null; }
  }, [solution]);
  /** 편집기 인스턴스 키 — LexicalComposer 는 initialEditorState 를 한 번만 읽는다.
      저장·재생성으로 정본이 바뀌면 리마운트해야 새 내용이 그려진다. */
  const lexKey = (p) => `${p}:${kind}:${id}:${detail?.updated_at ?? detail?.status ?? ''}`;
  /**
   * 그리지 못한 도판 — 자리가 둘이라 둘 다 본다.
   *  ① 최상위 `result.image_issues:[{index, issues[]}]`(계약 §4 D4) — 생성 산출이 쓰던 자리.
   *  ② image 블록 안 `issues`(BE 부록 2026-08-30) — spec 을 렌더하지 못하면 `svg:null` 과 함께 온다.
   *     **변형에도 실린다.** 종전에는 ①만 보아, 변형에서 도판이 빠진 이유를 화면이 말하지 못했다.
   * ①이 있으면 그것이 정본이다(같은 사실을 두 번 세지 않는다).
   */
  const imageIssues = useMemo(() => {
    const top = Array.isArray(values.image_issues) ? values.image_issues : [];
    if (top.length > 0) return top;
    try {
      return [
        ...collectImageIssues(problem),
        ...(FIGURE_SVG_EDITOR ? collectImageIssues({ stem: solution?.content }).map((issue) => ({ ...issue, field: 'solution' })) : []),
      ];
    } catch (_) { return []; }
  }, [values.image_issues, problem, solution]);
  const imgMode = detail?.image_mode ? IMAGE_MODE_LABEL[detail.image_mode] : null;
  const reviewed = detail?.review_status === 200 || detail?.review_status === 400;

  const dirty = answerText !== seedRef.current.answer
    || difficulty !== seedRef.current.difficulty
    || description !== seedRef.current.description
    || answerType !== seedRef.current.answerType
    || choiceSubtype !== seedRef.current.choiceSubtype
    || JSON.stringify(conceptIds) !== JSON.stringify(seedRef.current.conceptIds);
  // 부모(목록)가 접기·이동 전에 물을 수 있도록 알린다 — 카드는 언마운트되면 편집이 사라진다
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  useEffect(() => { onDirtyRef.current?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyRef.current?.(false), []);

  const buildPatch = () => {
    const s = seedRef.current;
    const patch = {};
    // 저장값은 다듬어 보낸다 — " 3 "을 그대로 보내면 화면상 같은 값인데도 저장 후 dirty 가 안 풀린다
    if (answerText !== s.answer) patch.answer = answerText.trim() === '' ? null : answerText.trim();
    if (difficulty !== s.difficulty) patch.difficulty = difficulty === '' ? null : Number(difficulty);
    if (description !== s.description) patch.description = description.trim() === '' ? null : description.trim();
    if (answerType !== s.answerType || choiceSubtype !== s.choiceSubtype) {
      // problem 은 통째 치환이라 병합본 위에 유형만 얹는다(본문·선지를 잃지 않게).
      //
      // **블록은 손대지 않는다 — 그대로 실려 나간다.** 계약 §4(D7): spec 이 없는 `{kind:'image'}`
      // 자리표시자 블록을 저장 때 떨어뜨리면, 승인에서 원본 그림을 다시 붙일 자리가 사라져 그림이
      // 유실된다(`result_edited.problem` 을 보낸 변형에는 서버가 원본 그림을 재부착하지 않는다).
      // 이 얕은 복사는 stem·choices 를 거르지 않으므로 지금은 안전하다 — 앞으로도 여기서 블록을
      // 필터링하지 마라.
      const next = { ...(values.problem || {}) };
      if (answerType === '') delete next.answerType; else next.answerType = answerType;
      if (answerType === 'multiple_choice' && choiceSubtype !== '') next.choiceSubtype = choiceSubtype;
      else delete next.choiceSubtype;
      patch.problem = next;
    }
    // 개념은 **배열 그대로** 보낸다. 빈 배열 `[]` 이 "개념 없음"이고, `null` 은 그 키의 수정 철회
    // (=원본 승계값 복귀)로 읽힌다(회신 §2 3-1-c). 둘을 섞으면 "개념을 지웠는데 원본 개념이
    // 되살아나는" 화면이 된다.
    if (JSON.stringify(conceptIds) !== JSON.stringify(s.conceptIds)) patch.concept_ids = conceptIds;
    return patch;
  };

  const save = async ({ quiet = false } = {}) => {
    if (busy || !detail) return detail;
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      if (!quiet) setToast({ type: 'info', text: '변경 사항이 없습니다.' });
      return detail;
    }
    setBusy('save');
    setToast(null);
    try {
      // 낙관락 — 직전 상세의 updated_at 을 함께 보낸다. 다른 검수자가 먼저 고쳤으면 409(stale).
      // 없이도 받지만(하위호환), 안 보내면 나중 저장이 앞 저장을 말없이 덮는다.
      const body = detail.updated_at ? { ...patch, expected_updated_at: detail.updated_at } : patch;
      let d = await api.patch(id, body, getAccessToken);
      if (d === AUTH_REDIRECT) return null;
      // 계약상 PATCH 응답은 갱신된 상세다. 그래도 형태가 어긋나면 재조회로 보강한다.
      if (!d || (!d.result && !d.result_edited)) d = await api.fetch(id, getAccessToken);
      if (String(id) === String(idRef.current)) {
        setDetail(d);
        seed(d);
        if (!quiet) setToast({ type: 'info', text: '저장되었습니다.' });
      }
      return d;
    } catch (e) {
      // 409 의 사유는 봉투의 data.reason 으로 온다(계약 §4 D6 · §3). 낙관락은 `stale` 하나뿐이지만,
      // 사유를 안 가리면 나중에 다른 갈래가 생겼을 때 "다른 사람이 먼저 수정했습니다"라는 거짓말을
      // 하게 된다. 사유가 아예 없는 409 는 배포 이전 행의 낙관락으로 보고 stale 로 다룬다.
      const reason = e?.data?.reason ?? null;
      if (reason === 'stale' || (e?.httpStatus === 409 && reason == null)) {
        // 편집을 덮어쓰지 않는다 — 사용자가 남의 수정을 보고 다시 판단하게 한다
        setToast({
          type: 'error',
          text: '다른 사람이 먼저 수정했습니다 — [새로 읽기]로 최신 내용을 확인한 뒤 다시 저장해 주세요.',
        });
        setStale(true);
        return null;
      }
      if (e?.httpStatus === 409) {
        // 아는 사유가 아니다 — 서버 문구를 그대로 보여 준다. 낙관락으로 단정하지 않는다.
        setToast({ type: 'error', text: e.message || '지금 상태로는 저장할 수 없습니다 — 최신 상태를 다시 읽어 주세요.' });
        return null;
      }
      setToast({ type: 'error', text: e.message || '저장에 실패했습니다.' });
      return null;
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    if (busy || !detail) return;
    if (!window.confirm(
      '이 문제를 문제은행에 새 문제로 등록합니다.\n'
      + (kind === 'variation' ? '원본 문제는 그대로 두고 새 문제가 생깁니다.\n' : '')
      + (dirty ? '저장하지 않은 수정도 함께 저장됩니다.\n' : '')
      + '\n승인할까요?',
    )) return;
    if (dirty) {
      const saved = await save({ quiet: true });
      if (!saved) return;
    }
    setBusy('approve');
    setToast(null);
    try {
      const d = await api.approve(id, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setApprovedId(d.problem_id ?? null);
      onApproved && onApproved(d.problem_id);
      onChanged && onChanged();
      // 갱신 실패가 성공 사실을 덮지 않도록 조용한 재조회는 마지막에, 실패해도 토스트를 남긴다
      await load({ silent: true, keepToast: true });
      setToast({
        type: 'info',
        text: d.problem_id != null ? `문제은행에 등록했습니다 — 새 문제 #${d.problem_id}` : '문제은행에 등록했습니다.',
      });
    } catch (e) {
      // 승인 409 갈래(계약 §4 D4): 본문에 「[그림]」 자리표시자가 남아 있으면 등록을 막는다 —
      // 그대로 통과시키면 그림 자리에 글자만 남은 문제가 문제은행에 들어간다.
      if (FIGURE_SVG_EDITOR) {
        setToast({ type: 'error', text: figureApprovalError(e) });
      } else if (e?.data?.reason === 'image_placeholder') {
        setToast({
          type: 'error',
          text: '본문에 [그림] 자리표시자가 남아 있어 등록하지 못했습니다 — 그림 블록을 지우거나 도판 명세를 고친 뒤 다시 승인하세요.',
        });
      } else {
        setToast({ type: 'error', text: e.message || '승인에 실패했습니다.' });
      }
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    if (busy || !detail) return;
    if (!window.confirm('이 결과를 폐기할까요? 목록에는 폐기 상태로 남습니다.')) return;
    setBusy('reject');
    setToast(null);
    try {
      const d = await api.reject(id, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      await load({ silent: true, keepToast: true });
      setToast({ type: 'info', text: '폐기했습니다.' });
      onChanged && onChanged();
    } catch (e) {
      setToast({ type: 'error', text: e.message || '폐기에 실패했습니다.' });
    } finally {
      setBusy(null);
    }
  };

  const retry = async () => {
    if (busy || !detail) return;
    setBusy('retry');
    setToast(null);
    try {
      const d = await api.retry(id, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      await load({ silent: true, keepToast: true });
      setToast({ type: 'info', text: '다시 생성을 요청했습니다.' });
      onChanged && onChanged();
    } catch (e) {
      setToast({ type: 'error', text: e.message || '재시도에 실패했습니다.' });
      // 다른 창에서 먼저 폐기했다(회신 §2) — 다시 읽어 눌러도 안 되는 버튼을 걷는다
      if (e?.data?.reason === 'rejected') {
        await load({ silent: true, keepToast: true });
        onChanged && onChanged();
      }
    } finally {
      setBusy(null);
    }
  };

  if (id == null) return null;
  if (loading) return <div className="p-4 text-sm text-gray-400">불러오는 중…</div>;
  if (error) {
    return (
      <div className="space-y-2 p-4">
        <p className="text-sm text-danger">{error}</p>
        <Button size="xs" variant="secondary" onClick={() => load()}>다시 시도</Button>
      </div>
    );
  }
  if (!detail) return null;

  const st = getJobStatusDisplay(detail.status);
  const rv = getReviewStatusDisplay(detail.review_status);

  return (
    <div className="space-y-3">
      {/* 상태 머리 — 알약은 문제 v2 **검색 목록 카드**의 얼굴이다(ProblemSearchPanel — 9px · px-1.5,
          난이도는 이름 없이 「난이도 4」. 오너 지시 2026-10-09). 한 줄의 알약은 같은 층위라 일곱 가지를
          모두 같은 크기로 둔다 — 셋만 바꾸면 한 줄에 두 크기가 선다. 이름(「어려움」)은 아래 난이도 칸이 말한다. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${st.cls}`}>{st.text}</span>
        {rv && detail.review_status !== 100 && (
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${rv.cls}`}>{rv.text}</span>
        )}
        {detail.level != null && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-600">강도 L{detail.level}</span>
        )}
        {/* 난이도 — 아래 select 로도 고칠 수 있지만 그쪽은 「고치는 자리」고, 머리는 「지금 무엇인가」를
            은행과 같은 색(difficultyBadgeClass)으로 말한다. */}
        {values.difficulty != null && (
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(values.difficulty)}`}>
            난이도 {values.difficulty}
          </span>
        )}
        {typeLabel && (
          <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-medium text-indigo-700">{typeLabel}</span>
        )}
        {imgMode && (
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${imgMode.cls}`} title={imgMode.hint}>{imgMode.text}</span>
        )}
        {/* 등록된 문제로 가는 길 — 배지가 아니라 **링크**다(오너 지시 2026-08-30).
            승인하면 이 산출은 은행의 문제가 되고, 그때부터 검수 카드는 그 문제의 「출생지」다.
            번호만 적어 두면 사용자가 목록으로 돌아가 그 번호를 손으로 찾아야 했다. */}
        {detail.approved_problem_id != null && (
          onOpenProblem ? (
            <button
              type="button"
              onClick={() => onOpenProblem(detail.approved_problem_id)}
              title="등록된 문제 열기"
              className="inline-flex items-center gap-0.5 rounded-full bg-green-50 px-1.5 py-0.5 text-[9px] font-medium text-green-700 transition-colors hover:bg-green-100 hover:text-green-900"
            >
              등록 <ProblemIdLink id={detail.approved_problem_id} chip linked={false} />
              <ArrowUpRight aria-hidden="true" className="size-3" />
            </button>
          ) : (
            <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[9px] text-green-700">
              등록 <ProblemIdLink id={detail.approved_problem_id} chip linked={false} />
            </span>
          )
        )}
      </div>

      {/* 라이브 리전은 항상 마운트해 두고 문구만 바꾼다 — 내용과 함께 삽입되면 낭독이 불안정하다 */}
      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>

      {/* 승인 결과 — 이동은 사용자가 고른다(자동 점프하면 남은 후보 검수가 끊긴다) */}
      {approvedId != null && (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
          새 문제 <ProblemIdLink id={approvedId} chip linked={false} /> 로 등록되었습니다.
          {onOpenProblem && (
            <button type="button" onClick={() => onOpenProblem(approvedId)} className="ml-auto font-medium underline hover:text-green-900">
              새 문제 보기
            </button>
          )}
        </div>
      )}

      {/* 진행 중 */}
      {polling && (
        <div aria-live="polite" aria-busy="true" className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          <RotateCw aria-hidden="true" className="size-4 animate-spin text-gray-400" />
          {/* 데몬은 직렬이고 대량 OCR 이 먼저다(계약 §4 「데몬」 — BE-20260824 의 "병렬" 문구 정정).
              "수십 초"만 말하면 앞 작업에 밀린 대기를 고장으로 읽는다 */}
          {waitedLong
            ? '예상보다 오래 걸립니다 — 대량 OCR 작업이 앞서 있으면 그만큼 밀립니다. 계속 기다리거나, 목록에서 상태를 다시 확인해 주세요.'
            : 'AI가 문제를 만들고 있습니다 — 한 건씩 차례로 처리되어 보통 수십 초 걸리며 자동으로 갱신됩니다.'}
        </div>
      )}

      {/* 실패 */}
      {detail.status === 500 && (
        <div role="alert" className="space-y-2 rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="text-sm text-red-700">생성 실패{detail.error ? ` — ${detail.error}` : ''}</p>
          <div className="flex items-center gap-2">
            {/* 폐기한 변형은 재시도가 409 다(BE-20261102 회신 §2) — 눌러도 안 되는 버튼을 세우지 않고 할 일을 말한다.
                생성(kind=generation)은 이 회신의 범위가 아니라 그대로 둔다. */}
            {kind === 'variation' && detail.review_status === 400 ? (
              <span className="text-xs text-gray-600">폐기한 문항이라 다시 생성할 수 없습니다 — 변형을 새로 요청하세요.</span>
            ) : canRunAi && (
              <Button size="xs" variant="secondary" onClick={retry} loading={busy === 'retry'} disabled={busy != null}>
                다시 생성
              </Button>
            )}
            {/* 계속 실패하는 건을 목록에서 치울 길 — 폐기는 상태 제한이 없다(계약 §2). 이미 정리된 건에는 치울 것이 없다 */}
            {canApprove && !reviewed && (
              <Button size="xs" variant="secondary" onClick={reject} loading={busy === 'reject'} disabled={busy != null}>
                폐기
              </Button>
            )}
          </div>
        </div>
      )}

      {stale && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <span className="min-w-0 flex-1">
            내가 읽은 뒤 다른 사람이 이 건을 고쳤습니다. 지금 저장하면 그 수정을 덮게 되어 막았습니다.
          </span>
          <Button
            size="xs"
            variant="secondary"
            onClick={() => { setStale(false); load(); }}
            disabled={busy != null}
          >
            새로 읽기(내 편집 버림)
          </Button>
        </div>
      )}

      {/* 아는 어느 상태도 아닐 때 — 빈 카드로 두면 사용자는 고장인지 진행 중인지 알 수 없다 */}
      {!polling && detail.status !== 200 && detail.status !== 500 && (
        <div role="status" className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm text-gray-600">알 수 없는 상태입니다(코드 {String(detail.status)}).</p>
          <Button size="xs" variant="secondary" onClick={() => load()}>다시 불러오기</Button>
        </div>
      )}

      {/* 검수 본문 */}
      {detail.status === 200 && (
        <>
          {/* 원본 대조 — 변형만(생성은 무에서 출제라 원본이 없다) */}
          {kind === 'variation' && (sourceDoc?.lexical || detail.source_text) && (
            <details className="rounded-xl border border-gray-200 bg-gray-50 p-2" open>
              <summary className="cursor-pointer text-xs font-medium text-gray-500">
                원본 문제(대조용)
                {sourceProblemId != null && (
                  <ProblemIdLink id={sourceProblemId} chip linked={false} className="ml-1 text-gray-500" />
                )}
              </summary>
              {/* 원본도 **결과와 같은 편집기**로 그린다 — 위아래가 다른 렌더러면 대조가 되지 않는다.
                  본문을 받지 못했을 때만 종전 평문(source_text)으로 내려간다. */}
              {sourceDoc?.lexical ? (
                <div className="mt-1 rounded-xl border border-gray-200 bg-white">
                  <OcrLexicalEditor
                    key={`src:${sourceProblemId}`}
                    initialState={sourceDoc.lexical}
                    label="원본 본문"
                    readOnly
                  />
                </div>
              ) : (
                <SourceTextView text={detail.source_text} />
              )}
              {detail.image_mode === 'reuse' && (
                <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
                  <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                  원본 그림을 그대로 쓰는 변형입니다 — 조건이 바뀌었다면 그림과 어긋나지 않는지 확인하세요.
                </p>
              )}
            </details>
          )}

          {/* 변형 결과 본문 — 문제은행 [문제 보기] 탭의 보기 모드와 **같은 편집기**로 그린다.
              readOnly 라 툴바가 없고, 그림 편집 스위치(figureEdit)도 주지 않는다: 이 그림은
              아직 은행에 없어 그림 API 가 가리킬 대상이 없다. */}
          <div>
            {/* 제목 줄의 짜임도 [문제 보기] 탭과 같다 — 이름 왼쪽, [미리보기] 오른쪽 끝. */}
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">{kind === 'variation' ? '변형 결과' : '생성 결과'}</span>
              <span className="ml-auto" />
              {/* 편집기는 **화면 폭**으로 그린다 — 지면은 한 단 423px·글자 18px 라 줄이 다른
                  자리에서 꺾인다. 「종이에 어떻게 앉는가」는 이 창으로 본다. */}
              <Button
                size="xs"
                variant="secondary"
                onClick={() => setPreviewOpen(true)}
                disabled={!bodyLex}
                icon={<Eye className="size-3.5" />}
                title={bodyLex ? '지금 화면의 내용을 지면 모양으로 봅니다' : '미리보기로 그릴 수 있는 본문이 아닙니다'}
              >
                미리보기
              </Button>
            </div>
            {bodyLex ? (
              <div className="rounded-xl border border-gray-200">
                <OcrLexicalEditor key={lexKey('b')} initialState={bodyLex} label="본문" readOnly />
              </div>
            ) : problem ? (
              // 변환이 못 하는 형태 — 종전 렌더러로 강등한다(얼굴은 갈리지만 내용은 남는다)
              <div className="rounded-xl border border-gray-100 p-3">
                <BodyPayloadView payload={problem} />
              </div>
            ) : (
              <p className="rounded-xl border border-gray-100 p-3 text-sm text-gray-400">본문이 없습니다.</p>
            )}
          </div>

          {/* 그림 결함(계약 §4 D4) — 도판 명세가 없거나 무효라 그리지 못한 image 블록.
              이걸 안 보이면 「[그림]」 글자만 남은 산출이 정상 도판과 같은 얼굴로 승인을 지나간다.
              승인은 서버가 409(image_placeholder)로 막지만, 막힌 뒤에 이유를 아는 것보다 먼저 아는 편이 낫다. */}
          {FIGURE_SVG_EDITOR && <FigureResults figures={detail.figures} />}
          {imageIssues.length > 0 && (
            <div role="alert" className="space-y-1 rounded-xl border border-red-100 bg-red-50 p-3">
              <p className="flex items-start gap-1.5 text-xs font-medium text-red-700">
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                {FIGURE_SVG_EDITOR ? `그림 ${imageIssues.length}개를 그리지 못했습니다. 그림을 다시 만들거나 해당 블록을 제거해 주세요.` : `그림 ${imageIssues.length}개를 그리지 못했습니다 — 본문의 [그림] 블록을 지우거나 도판 명세를 고쳐야 승인됩니다.`}
              </p>
              <ul className="ml-5 list-disc space-y-0.5 text-[11px] text-red-700">
                {imageIssues.map((it, i) => (
                  <li
                    // eslint-disable-next-line react/no-array-index-key -- index 가 비어 오면 순서가 곧 정체다(SourceTextView 와 같은 이유)
                    key={`${it?.field || 'body'}-${it?.index ?? i}-${i}`}
                  >
                    {/* 불확실: index 가 0 기준인지 1 기준인지 계약에 없다 — 손대지 않고 그대로 찍는다 */}
                    {FIGURE_SVG_EDITOR ? `${it.field === 'solution' ? '풀이' : '본문'} 그림${it.figure_ref ? ` ${it.figure_ref}` : ''}` : it?.index != null ? `그림 블록 index ${it.index}` : '그림 블록'}
                    {' — '}
                    {Array.isArray(it?.issues) && it.issues.length > 0 ? it.issues.join(' · ') : FIGURE_SVG_EDITOR ? '그림을 확인해 주세요.' : '도판 명세 없음'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 정답 — 유형별 입력 보조(§4-2) */}
          <div>
            <div className="mb-1 text-xs font-medium text-gray-500">정답</div>
            {problem?.answerType === 'multiple_choice' && (
              <div className="mb-1.5 flex flex-wrap items-center gap-1">
                {/* 마커는 선지의 no 에서 뽑는다 — 위치로 매기면 no 가 1부터가 아니거나 비어 있을 때
                    본문 렌더러(BlockRenderer 는 no 기준)와 번호가 어긋난다.
                    이제 서버가 `choices:[{no:int, content:Block[]}]` 를 보장하지만(계약 §4 D5),
                    그 정규화 이전에 만들어진 행에는 no 가 비어 있을 수 있어 폴백은 남긴다. */}
                {(Array.isArray(problem.choices) && problem.choices.length > 0
                  ? problem.choices.map((c, i) => CIRCLED[(c?.no ?? i + 1) - 1])
                  : CIRCLED.slice(0, 5)).filter(Boolean).map((mark) => (
                  <button
                    key={mark}
                    type="button"
                    onClick={() => setAnswerText(answerText.trim() === mark ? '' : mark)}
                    disabled={busy != null || reviewed}
                    aria-pressed={answerText.trim() === mark}
                    className={`size-8 rounded-full border text-sm transition-colors disabled:opacity-40 ${
                      answerText.trim() === mark ? 'border-primary bg-primary font-bold text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary/50'
                    }`}
                  >
                    {mark}
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              disabled={busy != null || reviewed}
              placeholder={problem?.answerType === 'descriptive' ? '대표 정답(풀이가 본체입니다)' : '정답'}
              className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
            />
            <div className="mt-1 text-xs text-gray-400">
              현재 값: <AnswerLine value={answerText} />
            </div>
            <p className="mt-0.5 text-[10px] text-gray-400">
              정답·난이도를 비워 저장하면 값이 지워지는 것이 아니라{' '}
              <b>{kind === 'variation' ? '원본에서 물려받은 값으로 되돌아갑니다' : 'AI 원본 값으로 되돌아갑니다'}</b>(수정 철회).
            </p>
          </div>

          {/* 난이도 · 유형 */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <span className="text-xs font-medium text-gray-500">난이도</span>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                disabled={busy != null || reviewed}
                className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
              >
                {/* 변형의 난이도는 AI 추측이 아니라 원본 승계값이다(계약 §4 D1) — 문구를 갈라 쓴다 */}
                <option value="">{kind === 'variation' ? '원본 승계값으로 되돌리기' : 'AI 값으로 되돌리기'}</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{difficultyOptionLabel(n)}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <span className="text-xs font-medium text-gray-500">유형</span>
              <select
                value={answerType}
                onChange={(e) => {
                  const v = e.target.value;
                  setAnswerType(v);
                  if (v !== 'multiple_choice') setChoiceSubtype('');
                }}
                disabled={busy != null || reviewed}
                className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
              >
                <option value="">유형 지우기</option>
                {Object.entries(ANSWER_TYPE_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            {answerType === 'multiple_choice' && (
              <label className="flex items-center gap-1.5 text-sm text-gray-600">
                <span className="text-xs font-medium text-gray-500">세부</span>
                <select
                  value={choiceSubtype}
                  onChange={(e) => setChoiceSubtype(e.target.value)}
                  disabled={busy != null || reviewed}
                  className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
                >
                  <option value="">세부 지우기</option>
                  {Object.entries(CHOICE_SUBTYPE_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </label>
            )}
          </div>
          {/* 계약 §4 D1: 변형의 난이도·개념은 모델이 추측한 값이 아니라 원본 문제에서 복사된 값이다.
              이걸 모르면 검수자가 "AI가 정한 개념"이라 보고 매번 다시 고른다 */}
          {kind === 'variation' && (
            <p className="-mt-2 text-[10px] text-gray-400">
              난이도·개념은 AI가 고른 값이 아니라 <b>원본 문제에서 물려받은 값</b>입니다 — 난이도는 요청에서 「쉽게/어렵게」를 골랐을 때만 원본 ±1 입니다.
            </p>
          )}
          {(answerType !== seedRef.current.answerType || choiceSubtype !== seedRef.current.choiceSubtype) && (
            <p className="-mt-2 text-[10px] text-amber-600">
              유형만 바꿔도 본문(problem)이 통째로 저장됩니다 — 선지·본문은 지금 보이는 그대로 유지됩니다.
            </p>
          )}

          {/* 개념 — 승인 시 새 문제에 붙는 값이라 여기서 고칠 수 있어야 한다 */}
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">개념</span>
              {!reviewed && (
                <button
                  type="button"
                  onClick={() => setConceptEdit((v) => !v)}
                  className="text-[10px] text-primary underline hover:text-primary/70"
                >
                  {conceptEdit ? '접기' : '고치기'}
                </button>
              )}
              {JSON.stringify(conceptIds) !== JSON.stringify(seedRef.current.conceptIds) && (
                <button
                  type="button"
                  onClick={() => setConceptIds(seedRef.current.conceptIds)}
                  className="text-[10px] text-gray-400 underline hover:text-gray-600"
                >
                  되돌리기
                </button>
              )}
            </div>
            {conceptEdit && !reviewed ? (
              <ConceptPicker
                value={conceptIds}
                onChange={setConceptIds}
                disabled={busy != null}
                emptyText="개념 없음 — 승인해도 개념이 붙지 않습니다"
                names={(cid) => conceptNameOf(detail, cid)}
                listInFlow /* 바로 아래 풀이 칸이 붙는다 — 띄우면 그 밑에 깔린다(AI 문제 생성 창과 같은 구조, 2026-09-15) */
              />
            ) : (
              // 배지는 [문제 보기] 탭·시험지 통계 열과 **같은 부품**이다 — [순번][유형] 두 칸 +
              // 이름, 누르면 그 개념의 설명이 뜬다. 순번·유형은 개념 사전에서 오고, 사전을 못
              // 받았으면 이름만 남는다(그 상태가 종전 칩과 같은 정보량이라 화면이 나빠지지 않는다).
              <div className="flex flex-wrap gap-1.5">
                {conceptIds.length === 0
                  ? <span className="text-xs text-gray-400">개념 없음</span>
                  : conceptIds.map((cid) => {
                    const mc = matchConcept(conceptDict, cid);
                    return (
                      <ConceptBadge
                        key={cid}
                        name={conceptNameOf(detail, cid) || `#${cid}`}
                        order={mc?.order ?? null}
                        type={mc?.concept_type ?? null}
                        compact
                        onClick={() => openConcept(cid)}
                      />
                    );
                  })}
              </div>
            )}
          </div>

          {/* 풀이 — 본문과 같은 규칙이다. 높이를 자르지 않는 것도 [문제 보기] 탭과 같다:
              종전의 max-h-72 는 이 화면에만 있던 것이라, 같은 풀이가 검수와 은행에서 다르게 잘렸다. */}
          <div>
            <div className="mb-1 text-xs font-medium text-gray-500">풀이</div>
            {solutionLex ? (
              <div className="rounded-xl border border-gray-200">
                <OcrLexicalEditor key={lexKey('s')} initialState={solutionLex} label="풀이" readOnly />
              </div>
            ) : solution ? (
              <div className="rounded-xl border border-gray-100 p-3">
                <SolutionPayloadView payload={solution} />
              </div>
            ) : (
              <p className="rounded-xl border border-gray-100 p-3 text-sm text-gray-400">생성된 풀이가 없습니다.</p>
            )}
          </div>

          {/* 요지 */}
          <div>
            <div className="mb-1 text-xs font-medium text-gray-500">{kind === 'variation' ? '변형 요지' : '출제 의도'}</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              disabled={busy != null || reviewed}
              placeholder="비워 두면 AI가 적은 내용이 그대로 남습니다"
              className="w-full rounded-xl border p-2 text-xs focus:border-primary focus:outline-none disabled:bg-gray-50"
            />
          </div>

          {/* 액션 */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            {canApprove && (
              <button
                type="button"
                onClick={reject}
                disabled={busy != null || reviewed}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 className="size-3.5" />
                {busy === 'reject' ? '폐기 중…' : '폐기'}
              </button>
            )}
            {dirty && !reviewed && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">수정됨 · 미저장</span>}
            {reviewed && (
              <span className="text-xs text-gray-400">
                {detail.review_status === 200 ? '이미 승인된 결과입니다.' : '폐기된 결과입니다.'}
              </span>
            )}
            <span className="ml-auto" />
            <Button size="sm" variant="secondary" onClick={() => save()} loading={busy === 'save'} disabled={busy != null || !dirty || reviewed}>
              저장
            </Button>
            {canApprove && (
              <Button size="sm" variant="success" onClick={approve} loading={busy === 'approve'} disabled={busy != null || reviewed} icon={<Check className="size-3.5" />}>
                승인 — 문제로 등록
              </Button>
            )}
          </div>
        </>
      )}

      {/* 지면 미리보기 — **지금 화면의 값**을 비춘다(검수에서 고친 난이도·개념이 반영된다).
          본문 자체는 아직 저장 전 편집분이 아니라 정본이다: 이 카드에서 본문을 고치는 길은
          유형 전환뿐이고, 그것은 선지·본문을 그대로 둔다. */}
      {previewOpen && (
        <VariationPreviewModal
          isOpen
          onClose={() => setPreviewOpen(false)}
          title={kind === 'variation' ? '변형 결과 미리보기' : '생성 결과 미리보기'}
          /* 등록된 뒤에는 진짜 번호를 넘긴다 — 창의 머리에서 그 문제로 갈 수 있게(링크).
             등록 전에는 번호 자체가 없어 대신할 말만 준다. */
          problemId={detail.approved_problem_id ?? null}
          idLabel={detail.approved_problem_id != null ? null : '아직 등록 전'}
          bodyLexical={bodyLex}
          difficulty={difficulty === '' ? (values.difficulty ?? null) : Number(difficulty)}
          conceptNames={conceptIds.map((cid) => ({ concept_id: cid, name: conceptNameOf(detail, cid) || `#${cid}` }))}
        />
      )}

      {/* 개념 설명 — 배지를 누르면 뜬다. data 를 주면 모달이 자체 조회(v1)를 건너뛴다.
          이 화면이 v2 전용인 근거다(오너 방침 2026-08-26 — v1 사전을 끌어오지 않는다). */}
      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          data={conceptDetail.data}
          loading={conceptDetail.loading}
        />
      )}
    </div>
  );
}

export default VariationReviewCard;
